import { useCueDiagnostics } from '../../lib/cue/useCueDiagnostics';
import { useActivityBlink } from '../../lib/cue/useActivityBlink';
import { useStore } from '../../store/StoreContext';
import {Signal, SignalLow} from "lucide-react";

// A small three-state Cue status glyph for one channel, shown on each row of
// ChannelsListScreen. Green once this tab has joined the channel's Cue room
// and the socket is live; amber while the join is still pending (or a
// reconnect is in flight after a drop); red when the socket is down. It
// also briefly pulses whenever a signal relays in for the channel, so an
// incoming message is visible at a glance from the list.
//
// Clickable: red/amber means the client is either down or still trying to
// rejoin on its own backoff (up to 30s between attempts, see client.ts's
// RECONNECT_MAX_MS) - a click runs reconnectCue() (CueClient.forceReconnect)
// immediately instead of waiting that out. A no-op while already green
// (nothing to reconnect), so it's never destructive to click regardless of
// state.
export function ConnectionDot({ channelId, className = '' }: { channelId: string; className?: string }) {
  const { connectionState, rooms } = useCueDiagnostics();
  const { reconnectCue } = useStore();
  const blinking = useActivityBlink(channelId);
  const joined = rooms[channelId] === 'joined';

  const color = connectionState === 'disconnected' ? 'text-red-500' : joined ? 'text-emerald-500' : 'text-amber-500';

  const label =
    connectionState === 'disconnected'
      ? 'Offline - click to reconnect'
      : joined
        ? 'Listening for signals'
        : 'Connecting - click to retry now';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!joined) reconnectCue();
      }}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full bg-white dark:bg-[#1c1c1e] ring-2 ring-white dark:ring-[#1c1c1e] ${className}`}
      aria-label={label}
      title={label}
    >
      <span className={`leading-none text-[11px] font-bold ${color} ${blinking ? 'motion-safe:animate-pulse' : ''}`} aria-hidden>
        {connectionState === 'connected' ? <Signal size={16} className="fill-current" /> : <SignalLow size={16} />}
      </span>
    </button>
  );
}
